import { useRef, useState, type PointerEvent } from 'react';
import {
  Alert,
  Button,
  Card,
  Icon,
  Table,
  cn,
  useDragScroll,
  type Column,
} from '@evogard/ui';
import type { ManagementFlow } from '@evogard/contract';
import {
  CLAIM_DEPARTMENTS,
  CLAIM_FLOW_NODES,
  count,
  type ClaimDepartment,
  type ClaimFlowNode,
  type ClaimFlowPeriod,
} from '@evogard/domain';
import { LINE_LABEL, LINE_TONE } from './managementLabels.js';
import { useManagementFlow } from './useManagement.js';

/**
 * O fluxo do sinistro (26/10/2026): o gráfico principal do Painel da Gestão do sinistro.
 *
 * Um Sankey de poucas etapas — Análise, Reparo, Indenização e os desfechos —
 * sobre a COORTE dos sinistros abertos no período (`summarizeClaimFlow`, em
 * `@evogard/domain`, diz o que conta onde). A largura de cada faixa é quantos
 * passaram por ali; o que sai do fluxo (negativa e desistência) desce para
 * baixo, e a parte clara da barra de cada departamento é quem ainda está em
 * processo, tendo chegado até ele.
 *
 * As cores seguem o mapa: cada departamento na cor da linha dele, o concluído
 * no verde de estado e a saída no vermelho. Nenhuma é a única pista — cada nó
 * tem nome e número escritos, e "Ver tabela" troca a figura pelos números.
 */

const W = 1000;
const H = 340;
const TOP = 46;
/** A faixa de baixo, onde descem as saídas, com os rótulos delas. */
const BOTTOM = 96;
const BAR = 14;

/** Onde cada nó fica: os de cima pela coluna, as saídas no pé, deitadas. */
// @valor — as chaves são os valores de `ClaimFlowNode`
const PLACE: Readonly<Record<ClaimFlowNode, { x: number; low?: true }>> = {
  analise: { x: 170 },
  reparo: { x: 430 },
  indenizacao: { x: 690 },
  concluido: { x: W - 40 - BAR },
  negado: { x: 480, low: true },
  desistencia: { x: 740, low: true },
};

// @valor — as chaves são os valores de `ClaimFlowNode`
const NODE_LABEL: Readonly<Record<ClaimFlowNode, string>> = {
  ...LINE_LABEL,
  concluido: 'Concluído',
  negado: 'Negativa',
  desistencia: 'Desistência',
};

const isDepartment = (n: ClaimFlowNode): n is ClaimDepartment =>
  (CLAIM_DEPARTMENTS as readonly string[]).includes(n);

// @valor — as chaves são os valores de `ClaimFlowNode`
const NODE_FILL: Readonly<Record<ClaimFlowNode, string>> = {
  analise: LINE_TONE.analise.head,
  reparo: LINE_TONE.reparo.head,
  indenizacao: LINE_TONE.indenizacao.head,
  concluido: 'fill-sucesso',
  negado: 'fill-erro',
  desistencia: 'fill-erro',
};

const linkTone = (to: ClaimFlowNode) =>
  to === 'concluido'
    ? 'stroke-sucesso'
    : PLACE[to].low
      ? 'stroke-erro'
      : 'stroke-texto-tenue';

const r1 = (n: number) => Math.round(n * 10) / 10;
const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

interface Tip {
  x: number;
  y: number;
  lines: string[];
}

/** O período é do painel inteiro (`ManagementPanel`): todos os gráficos olham a mesma coorte. */
export function ClaimFlowChart({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementFlow(days);
  const flow = query.data;
  const ready = flow && flow.total > 0 && !query.isError;

  return (
    <Card
      as="figure"
      title="O fluxo do sinistro"
      descricao={
        <>
          Por onde passaram os abertos nos últimos {days} dias e como terminaram. A
          largura é quantos passaram; o que sai desce em vermelho.
        </>
      }
      actions={
        ready && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar o fluxo">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !flow ? (
        <div
          className="animate-pulse rounded-borda bg-superficie-suave"
          style={{ height: H * 0.8 }}
          aria-busy="true"
        />
      ) : flow.total === 0 ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro aberto nos últimos {days} dias. Quando houver, o fluxo mostra
          por onde cada um passou e como terminou.
        </p>
      ) : asTable ? (
        <FlowTable flow={flow} />
      ) : (
        <FlowFigure flow={flow} />
      )}
      {flow && flow.total > 0 && (
        <figcaption className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-texto-suave">
          <span>
            {count(flow.total, 'sinistro aberto', 'sinistros abertos')} desde{' '}
            {flow.since.split('-').reverse().join('/')}
            {flow.thirdParty > 0 && `, ${flow.thirdParty} de terceiro`}.
          </span>
          <span className="flex items-center gap-1.5">
            <i aria-hidden className="h-1.5 w-4 rounded-pilula bg-texto-tenue/50" />
            seguiu no fluxo
          </span>
          <span className="flex items-center gap-1.5">
            <i aria-hidden className="h-1.5 w-4 rounded-pilula bg-sucesso/60" />
            concluído
          </span>
          <span className="flex items-center gap-1.5">
            <i aria-hidden className="h-1.5 w-4 rounded-pilula bg-erro/60" />
            saiu: negativa ou desistência
          </span>
          <span className="flex items-center gap-1.5">
            <i
              aria-hidden
              className={cn(
                'h-3 w-2 rounded-[2px] border',
                LINE_TONE.analise.soft,
                LINE_TONE.analise.border,
              )}
            />
            parte clara: ainda em processo
          </span>
        </figcaption>
      )}
    </Card>
  );
}

/** Quantos chegaram a cada nó: o começo é todo mundo; os outros, o que entrou. */
function nodeTotals(flow: ManagementFlow): Record<ClaimFlowNode, number> {
  const totals = Object.fromEntries(CLAIM_FLOW_NODES.map((n) => [n, 0])) as Record<
    ClaimFlowNode,
    number
  >;
  totals.analise = flow.total;
  for (const l of flow.links) if (l.to !== 'analise') totals[l.to] += l.total;
  return totals;
}

const inProcessOf = (flow: ManagementFlow, node: ClaimFlowNode) =>
  flow.inProcess.find((d) => d.department === node)?.total ?? 0;

function FlowFigure({ flow }: { flow: ManagementFlow }) {
  const [tip, setTip] = useState<Tip | null>(null);
  const frame = useRef<HTMLDivElement>(null);
  useDragScroll(frame);
  const totals = nodeTotals(flow);
  const scale = (H - TOP - BOTTOM) / flow.total;
  const size = (n: number) => Math.max(2, n * scale);

  /* As barras: em pé no alto, deitadas no pé. */
  const nodes = CLAIM_FLOW_NODES.filter((n) => totals[n] > 0 || isDepartment(n)).map(
    (n) => {
      const { x, low } = PLACE[n];
      const len = totals[n] > 0 ? size(totals[n]) : 0;
      return low
        ? { n, x, y: H - 34, w: len, h: BAR, low: true }
        : { n, x, y: TOP, w: BAR, h: len, low: false };
    },
  );
  const at = (n: ClaimFlowNode) => nodes.find((x) => x.n === n)!;

  /* As faixas, empilhadas na ordem dos nós: na saída, de cima para baixo; na chegada, idem. */
  const out: Partial<Record<ClaimFlowNode, number>> = {};
  const into: Partial<Record<ClaimFlowNode, number>> = {};
  const links = flow.links.map((l) => {
    const s = at(l.from);
    const t = at(l.to);
    const w = size(l.total);
    const y0 = s.y + (out[l.from] ?? 0) + w / 2;
    out[l.from] = (out[l.from] ?? 0) + w;
    const offset = into[l.to] ?? 0;
    into[l.to] = offset + w;
    const x0 = s.x + BAR;
    let d: string;
    if (t.low) {
      /* Desce e entra por cima da barra deitada: a faixa termina em pé. */
      const x1 = t.x + offset + w / 2;
      const y1 = t.y;
      d = `M${r1(x0)},${r1(y0)} C${r1(x0 + (x1 - x0) * 0.6)},${r1(y0)} ${r1(x1)},${r1(y0 + (y1 - y0) * 0.4)} ${r1(x1)},${r1(y1)}`;
    } else {
      const x1 = t.x;
      const y1 = t.y + offset + w / 2;
      const mx = (x0 + x1) / 2;
      d = `M${r1(x0)},${r1(y0)} C${r1(mx)},${r1(y0)} ${r1(mx)},${r1(y1)} ${r1(x1)},${r1(y1)}`;
    }
    return { ...l, d, w };
  });

  const show = (e: PointerEvent, lines: string[]) => {
    const box = (e.currentTarget as Element)
      .closest('[data-flow]')!
      .getBoundingClientRect();
    setTip({ x: e.clientX - box.left, y: e.clientY - box.top, lines });
  };

  return (
    <div className="relative" data-flow onPointerLeave={() => setTip(null)}>
      <div ref={frame} className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="mx-auto block max-h-[22rem] w-full min-w-[42rem]"
          role="img"
          aria-label={`Fluxo do sinistro: de ${flow.total} sinistros abertos, ${totals['concluido']} concluídos, ${totals.negado} negativas e ${totals.desistencia} desistências; os outros seguem em processo.`}
        >
          {/* O começo: o total de avisos em número grande, sem barra — a da Análise já é ele. */}
          <g
            onPointerMove={(e) =>
              show(e, [
                'Avisos abertos',
                `${count(flow.total, 'sinistro aberto', 'sinistros abertos')} nos últimos ${flow.days} dias`,
              ])
            }
          >
            <text
              x={PLACE.analise.x - 24}
              y={TOP + size(flow.total) / 2 + 6}
              textAnchor="end"
              className="fill-texto text-[40px] font-bold tabular-nums"
            >
              {flow.total}
            </text>
            <text
              x={PLACE.analise.x - 24}
              y={TOP + size(flow.total) / 2 + 26}
              textAnchor="end"
              className="fill-texto-suave text-[13px]"
            >
              avisos abertos
            </text>
          </g>
          {links.map((l) => (
            <path
              key={`${l.from}>${l.to}`}
              d={l.d}
              fill="none"
              strokeWidth={r1(l.w)}
              className={cn(
                'opacity-30 transition-opacity hover:opacity-60',
                linkTone(l.to),
              )}
              onPointerMove={(e) =>
                show(e, [
                  `${NODE_LABEL[l.from]} → ${NODE_LABEL[l.to]}`,
                  `${count(l.total, 'sinistro', 'sinistros')} · ${share(l.total, flow.total)}% dos abertos`,
                ])
              }
            />
          ))}
          {nodes.map((node) => {
            const total = totals[node.n];
            const open = isDepartment(node.n) ? inProcessOf(flow, node.n) : 0;
            const openH = open ? size(open) : 0;
            const lines = [
              NODE_LABEL[node.n],
              node.n === 'analise'
                ? `${count(total, 'sinistro aberto', 'sinistros abertos')} no período`
                : `${count(total, 'sinistro chegou', 'sinistros chegaram')} · ${share(total, flow.total)}% dos abertos`,
              ...(open
                ? [`${count(open, 'ainda em processo aqui', 'ainda em processo aqui')}`]
                : []),
            ];
            const end = node.n === 'concluido';
            const value = `${total} · ${share(total, flow.total)}%`;
            return (
              <g key={node.n} onPointerMove={(e) => show(e, lines)}>
                {node.h > 0 && (
                  <rect
                    x={node.x}
                    y={r1(node.y)}
                    width={r1(node.w)}
                    height={r1(node.h)}
                    rx={3}
                    className={NODE_FILL[node.n]}
                  />
                )}
                {openH > 0 && isDepartment(node.n) && (
                  <rect
                    x={node.x + 1}
                    y={r1(node.y + node.h - openH + 1)}
                    width={BAR - 2}
                    height={r1(Math.max(1, openH - 2))}
                    rx={2}
                    className={cn('fill-superficie opacity-70')}
                  />
                )}
                {node.low ? (
                  <text x={node.x} y={node.y + node.h + 18} className="text-[13px]">
                    <tspan className="fill-texto font-semibold">
                      {NODE_LABEL[node.n]}
                    </tspan>
                    <tspan dx={8} className="fill-texto-suave">
                      {total ? value : '0'}
                    </tspan>
                  </text>
                ) : (
                  <>
                    <text
                      x={end ? node.x + BAR : node.x}
                      y={node.y - 22}
                      textAnchor={end ? 'end' : 'start'}
                      className="fill-texto text-[13px] font-semibold"
                    >
                      {NODE_LABEL[node.n]}
                    </text>
                    <text
                      x={end ? node.x + BAR : node.x}
                      y={node.y - 7}
                      textAnchor={end ? 'end' : 'start'}
                      className="fill-texto-suave text-[12px]"
                    >
                      {total ? value : 'ninguém passou'}
                    </text>
                  </>
                )}
                {openH > 0 && (
                  <text
                    x={node.x + BAR + 6}
                    y={r1(node.y + node.h - openH / 2 + 4)}
                    className="fill-texto-suave text-[12px]"
                  >
                    {open} em processo
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {tip && (
        <div
          role="presentation"
          className="pointer-events-none absolute z-10 rounded-borda border border-borda bg-superficie px-3 py-2 text-xs shadow-flutuante"
          style={{ left: tip.x + 12, top: tip.y + 12 }}
        >
          {tip.lines.map((line, i) => (
            <p
              key={i}
              className={i === 0 ? 'font-semibold text-texto' : 'text-texto-suave'}
            >
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

interface FlowRow {
  node: ClaimFlowNode;
  total: number;
  open: number | null;
}

/** Os mesmos números, em tabela: o caminho de quem não lê a figura. */
function FlowTable({ flow }: { flow: ManagementFlow }) {
  const totals = nodeTotals(flow);
  const rows: FlowRow[] = CLAIM_FLOW_NODES.map((node) => ({
    node,
    total: totals[node],
    open: isDepartment(node) ? inProcessOf(flow, node) : null,
  }));
  const columns: Column<FlowRow>[] = [
    { title: 'Etapa', cell: (r) => NODE_LABEL[r.node], alignment: 'inicio' },
    { title: 'Chegaram', cell: (r) => r.total, alignment: 'fim' },
    {
      title: '% dos abertos',
      cell: (r) => `${share(r.total, flow.total)}%`,
      alignment: 'fim',
    },
    {
      title: 'Ainda em processo',
      cell: (r) => (r.open === null ? '—' : r.open),
      alignment: 'fim',
    },
  ];
  return (
    <Table
      columns={columns}
      items={rows}
      rowKey={(r) => r.node}
      label={`O fluxo do sinistro nos últimos ${flow.days} dias`}
      empty="Nenhum sinistro aberto no período."
    />
  );
}
